import { useEffect, useState } from 'react';

import { toShortPath } from '../utils/recordCodes';
import { siteOrigin } from '../platform';
import { entriesOf } from '../utils/repeatingGroupEntries';
import { isRepeatingGroup } from '../config/repeatingGroups';

/**
 * A deep link to one entry of a repeating group is the item's address with
 * `#entry-<id>` (KPR-680): the id keepr issued, which survives edits and
 * reordering. `linkedEntryId('#entry-k3m9x2qa7b')` is `'k3m9x2qa7b'`; any
 * other hash is `''`.
 */
export function linkedEntryId(hash) {
	const match = /^#entry-([a-z][0-9a-z]{9})$/.exec(String(hash || ''));
	return match ? match[1] : '';
}

/**
 * The address of one entry on an item: `<origin>/collections/<c>/items/<i>#entry-<id>`,
 * or `<origin>/i/<code>#entry-<id>` when the item's short code is known
 * (KPR-690). An entry has no code of its own: the id after the hash is the
 * whole of it, and the item's short URL carries the hash through.
 */
export function entryLink(collectionId, itemId, entryId) {
	if (!collectionId || !itemId || !entryId || typeof window === 'undefined') return '';
	return `${siteOrigin()}${toShortPath(`/collections/${collectionId}/items/${itemId}#entry-${entryId}`)}`;
}

/**
 * The entry id the address names when NO repeating group this person can see
 * holds it, else `''` (KPR-690). That is a link to an entry that was removed,
 * one that never was, or one the person cannot see: the three read alike, so
 * the notice says "no longer on this item, or you cannot see it". An item with
 * no visible group says nothing at all — a reader who cannot see a group sees
 * what they would see without the link.
 */
export function missingLinkedEntryId(hash, groupDefs, elements) {
	const id = linkedEntryId(hash);
	if (!id) return '';
	const groups = (Array.isArray(groupDefs) ? groupDefs : []).filter(isRepeatingGroup);
	if (!groups.length) return '';
	const found = groups.some(def => entriesOf(elements?.[def.name]).some(entry => entry?.id === id));
	return found ? '' : id;
}

/** The address's hash, followed. */
export function useAddressHash() {
	const [hash, setHash] = useState(() => (typeof window !== 'undefined' ? window.location.hash : ''));
	useEffect(() => {
		const onHash = () => setHash(window.location.hash);
		window.addEventListener('hashchange', onHash);
		return () => window.removeEventListener('hashchange', onHash);
	}, []);
	return hash;
}

/**
 * The entry the address names, when the list holds it: scrolled to once it is
 * there, and returned so the surface can open and mark it. `''` otherwise.
 * (Read from the address at mount and on `hashchange`; no router needed, so
 * the same hook serves the item form, the read view and a shared page.)
 */
export default function useLinkedEntry(entries) {
	const [id, setId] = useState(() => linkedEntryId(typeof window !== 'undefined' ? window.location.hash : ''));
	useEffect(() => {
		const onHash = () => setId(linkedEntryId(window.location.hash));
		window.addEventListener('hashchange', onHash);
		return () => window.removeEventListener('hashchange', onHash);
	}, []);
	const present = Boolean(id) && Array.isArray(entries) && entries.some(entry => entry?.id === id);
	// Scrolled to once it has a place on the page — a form lays itself out in
	// steps (panels, a layout that arrives late), so this waits for the entry
	// to be drawn — and again as the page settles, until the person scrolls or
	// types, whose place it must never take.
	useEffect(() => {
		if (!present) return undefined;
		const timers = [];
		let polls = 0;
		const reveal = () => document.getElementById(`entry-${id}`)?.scrollIntoView?.({ block: 'center' });
		const poll = () => {
			const el = document.getElementById(`entry-${id}`);
			if (el && el.getClientRects().length) {
				reveal();
				timers.push(setTimeout(reveal, 400), setTimeout(reveal, 1200));
				return;
			}
			polls += 1;
			if (polls < 80) timers.push(setTimeout(poll, 100));
		};
		poll();
		const stop = () => timers.forEach(clearTimeout);
		const events = ['wheel', 'touchstart', 'keydown', 'mousedown'];
		events.forEach(name => window.addEventListener(name, stop, { once: true, passive: true }));
		return () => {
			stop();
			events.forEach(name => window.removeEventListener(name, stop));
		};
	}, [present, id]);
	return present ? id : '';
}
